import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthLayout from "@/pages/auth/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForceReset() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!user) {
    navigate("/login");
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters");
    if (pw !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { new_password: pw });
      toast.success("Password updated. Welcome aboard!");
      await refreshUser();
      navigate("/dashboard");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Set a new password" subtitle="For your security, please choose a new password before continuing.">
      <form onSubmit={submit} className="space-y-5" data-testid="force-reset-form">
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          You're signed in with a temporary password. Set a permanent one to access your dashboard.
        </div>
        <div>
          <Label htmlFor="pw">New password</Label>
          <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5 h-12" data-testid="new-password-input" autoFocus />
          <p className="mt-1 text-xs text-slate-400">At least 8 characters, including letters and numbers.</p>
        </div>
        <div>
          <Label htmlFor="confirm">Confirm password</Label>
          <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-12" data-testid="confirm-password-input" />
        </div>
        {error && <p className="text-sm text-rose-600" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full h-12 bg-teal hover:bg-teal-700" data-testid="force-reset-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & continue"}
        </Button>
        <button type="button" onClick={async () => { await logout(); navigate("/login"); }} className="text-sm text-slate-500 hover:text-teal w-full">Sign out</button>
      </form>
    </AuthLayout>
  );
}
